export interface SerializedError {
  code: string
  message: string
  retryable: boolean
  context: Record<string, unknown>
  correlationId?: string
}

export interface AppErrorOptions {
  context?: Record<string, unknown>
  correlationId?: string
  cause?: unknown
}

export abstract class AppError extends Error {
  abstract readonly code: string
  readonly retryable: boolean = false
  readonly context: Record<string, unknown>
  readonly correlationId: string | undefined

  constructor(message: string, options: AppErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause })
    this.name = new.target.name
    this.context = options.context ?? {}
    this.correlationId = options.correlationId
    Object.setPrototypeOf(this, new.target.prototype)
  }

  toJSON(): SerializedError {
    const result: SerializedError = {
      code: this.code,
      message: this.message,
      retryable: this.retryable,
      context: this.context,
    }
    if (this.correlationId !== undefined) {
      result.correlationId = this.correlationId
    }
    return result
  }
}

export class ValidationError extends AppError {
  readonly code = 'VALIDATION_ERROR'
}

export class AuthError extends AppError {
  readonly code = 'AUTH_ERROR'
}

export class ForbiddenError extends AppError {
  readonly code = 'FORBIDDEN'
}

export class NotFoundError extends AppError {
  readonly code = 'NOT_FOUND'
}

export class ConflictError extends AppError {
  readonly code = 'CONFLICT'
}

export class RateLimitError extends AppError {
  readonly code = 'RATE_LIMIT'
  override readonly retryable = true
}

export class UpstreamError extends AppError {
  readonly code = 'UPSTREAM_ERROR'
  override readonly retryable = true
}

export class ChannelError extends AppError {
  readonly code = 'CHANNEL_ERROR'
}

export class PipelineError extends AppError {
  readonly code = 'PIPELINE_ERROR'
}

export class InternalError extends AppError {
  readonly code = 'INTERNAL_ERROR'
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError
}
